import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useSEO, breadcrumb } from "../lib/seo";
import { SoftwareCard } from "../components/SoftwareCard";
import { LANDINGS } from "../data/landings";

export default function KeywordLanding({ slug }) {
  const cfg = LANDINGS.find((l) => l.slug === slug);
  const [software, setSoftware] = useState([]);

  useEffect(() => {
    api.get("/software").then(({ data }) => setSoftware(data));
  }, []);

  useSEO({
    title: cfg?.seo_title || cfg?.h1,
    description: cfg?.seo_description,
    canonicalPath: `/${slug}`,
    jsonLd: cfg && [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: cfg.h1, path: `/${slug}` },
      ]),
      ...(cfg.faq && cfg.faq.length
        ? [{ "@type": "FAQPage", mainEntity: cfg.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }]
        : []),
    ],
  });

  if (!cfg) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-500">Page not found.</div>;

  const bySlug = Object.fromEntries(software.map((s) => [s.slug, s]));
  const featured = (cfg.featured_slugs || []).map((s) => bySlug[s]).filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid={`landing-${slug}`}>
      <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Buyer's guide</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">{cfg.h1}</h1>
      {cfg.intro_md && (
        <div className="prose-pool mt-5 max-w-2xl">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{cfg.intro_md}</ReactMarkdown>
        </div>
      )}

      {featured.length > 0 && (
        <div className="mt-10 space-y-4">
          {featured.map((s, i) => (
            <SoftwareCard key={s.slug} software={s} rank={i + 1} />
          ))}
        </div>
      )}

      {(cfg.sections || []).map((sec, i) => (
        <section key={i} className="mt-12">
          <h2 className="font-heading font-bold text-2xl text-slate-900">{sec.h2}</h2>
          <div className="prose-pool mt-3">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{sec.md}</ReactMarkdown>
          </div>
        </section>
      ))}

      {cfg.faq && cfg.faq.length > 0 && (
        <div className="mt-12 border-t border-border pt-8">
          <h2 className="font-heading font-bold text-2xl text-slate-900 mb-5">Frequently asked questions</h2>
          <div className="space-y-4">
            {cfg.faq.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-5">
                <h3 className="font-heading font-bold text-slate-900 text-sm">{f.q}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12 flex flex-wrap gap-2">
        <Link to="/best-pool-service-software" className="text-sm px-4 py-2 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-700 transition-colors duration-200">See the full 2026 ranking</Link>
        <Link to="/comparisons" className="text-sm px-4 py-2 rounded-full border border-border bg-white text-slate-700 font-semibold hover:border-sky-400 hover:text-sky-600 transition-colors duration-200">Head-to-head comparisons</Link>
      </div>
    </div>
  );
}
